import type { FlatFamilyResponse } from './flatFamily';

export type PropertyType = 'Apartment' | 'House' | 'Villa' | 'Studio';
export const propertyTypeOptions: ReadonlyArray<{ value: PropertyType; label: string }> = [
  { value: 'Apartment', label: 'Apartment' },
  { value: 'House', label: 'Haus' },
  { value: 'Villa', label: 'Villa' },
  { value: 'Studio', label: 'Studio' },
];

export type PropertyLifecycleStatus = 'Draft' | 'Active' | 'Paused' | 'Archived';
export type PropertyListLifecycle = 'Active' | 'Archived' | 'All';
export const propertyStatusOptions: ReadonlyArray<{
  value: PropertyLifecycleStatus;
  label: string;
}> = [
  { value: 'Draft', label: 'Entwurf' },
  { value: 'Active', label: 'Aktiv' },
  { value: 'Paused', label: 'Pausiert' },
  { value: 'Archived', label: 'Archiviert' },
];

export interface AddressDto {
  street: string;
  streetName: string;
  houseNumber: string;
  city: string;
  postalCode: string;
  countryCode: string;
  latitude?: number | null;
  longitude?: number | null;
}

export interface AddressSuggestionResponse {
  label: string;
  address: AddressDto;
  source: 'ExistingObject' | 'Provider';
  propertyGroupId?: string | null;
  isComplete: boolean;
  needsAddressReview: boolean;
  visiblePropertyCount?: number | null;
}

export interface AddressSearchRequest {
  query: string;
  postalCode?: string;
  city?: string;
  countryCode?: string;
}

export interface UpsertPropertyGroupRequest {
  name: string;
  address: AddressDto;
  needsAddressReview: boolean;
  /**
   * Weggelassen: beim Anlegen die Werte des Workspace, beim Ändern unverändert, beim Teilen die
   * des Quellobjekts.
   */
  currency?: string;
  timezone?: string;
  /** Nicht mitgeschickt heißt „unverändert" — nicht „zurück auf Default". */
  checkInTime?: string;
  checkOutTime?: string;
  /** Leerer String heißt ausdrücklich „löschen", weggelassen heißt „unverändert". */
  buildingAccessCode?: string;
  /** Leerer String löscht die jeweilige Fassung, weggelassen lässt sie unverändert. */
  houseRulesDe?: string;
  houseRulesEn?: string;
}

/**
 * Objekt-Struktur. Alle drei Wege sind serverseitig fail-closed, sobald das Objekt beim
 * Kanalmanager gebunden ist — die Vendor-Identität hängt am Objekt und würde beim Umhängen
 * ihre Bedeutung verlieren.
 */
export interface MergePropertyGroupsRequest {
  targetGroupId: string;
}

export interface SplitPropertyGroupRequest {
  propertyIds: string[];
  newGroup: UpsertPropertyGroupRequest;
}

/** Ein scope-sichtbares Apartment eines Objekts — nie ein für den Nutzer verstecktes (Threat Model §6.63). */
export interface ObjectApartmentSummary {
  id: string;
  name: string;
  adminDisplayName?: string | null;
  status: PropertyLifecycleStatus;
  /** Gesamtwohnung dieses Zimmers im Wohnungsverbund, sofern für den Aufrufer sichtbar. */
  wholeFlatPropertyId?: string | null;
}

export interface ObjectParentBindingResponse {
  bindingId: string;
  status: string;
  generation: number;
  desiredVersion: number;
  lastConfirmedVersion: number;
  lastError?: string | null;
}

export interface PropertyGroupResponse {
  id: string;
  name: string;
  address: AddressDto;
  needsAddressReview: boolean;
  /** Nur die für den Aufrufer sichtbaren Apartments; teilsichtbare Objekte leaken keine verborgenen Namen/Counts. */
  apartments: ObjectApartmentSummary[];
  visibleApartmentCount: number;
  currency: string;
  timezone: string;
  parentBinding?: ObjectParentBindingResponse | null;
  /** Gilt für alle Apartments des Objekts — Reinigung, Gastportal und Anreiseleitfaden lesen sie. */
  checkInTime: string;
  checkOutTime: string;
  /** Ob ein Haustürcode hinterlegt ist — der Wert selbst wird nie ausgeliefert. */
  hasBuildingAccessCode: boolean;
  houseRulesDe?: string | null;
  houseRulesEn?: string | null;
  /** Die automatische Positionssuche steht noch aus — ohne Pin heißt das „wird gesucht“. */
  positionLookupPending?: boolean;
}

export interface GuestExperienceSettingsResponse {
  propertyGroupId: string;
  welcomeTextDe?: string | null;
  welcomeTextEn?: string | null;
  reminder5dTextDe?: string | null;
  reminder5dTextEn?: string | null;
  finalReminderTextDe?: string | null;
  finalReminderTextEn?: string | null;
  postStayTextDe?: string | null;
  postStayTextEn?: string | null;
  reminder5dEnabled: boolean;
  finalReminderEnabled: boolean;
  postStayEnabled: boolean;
  hasLogo: boolean;
  concurrency?: number | null;
}

export interface UpdateGuestExperienceSettingsRequest extends Omit<
  GuestExperienceSettingsResponse,
  'propertyGroupId' | 'hasLogo' | 'concurrency'
> {
  expectedConcurrency?: number | null;
}

export interface GuestExperiencePreviewRequest {
  stage: 'Welcome' | 'Reminder5d' | 'FinalReminder' | 'PostStay';
  textDe?: string | null;
  textEn?: string | null;
}

export interface GuestExperiencePreviewResponse {
  german: string;
  english: string;
}

export interface PropertyResponse {
  id: string;
  tenantId: string;
  name: string;
  operationalName?: string | null;
  adminDisplayName?: string | null;
  publicationHealth?: string | null;
  slug: string;
  description?: string | null;
  propertyType: PropertyType;
  timezone: string;
  address: AddressDto;
  occupancyAdults: number;
  occupancyChildren: number;
  bedrooms: number;
  bathrooms: number;
  availableBedCount: number;
  availableSofaBedCount: number;
  checkInTime: string; // "HH:mm:ss"
  checkOutTime: string;
  currency: string;
  cleaningFee: number;
  baseNightlyRate: number;
  /** Wo das **Objekt** dieses Apartments beim Kanalmanager liegt (alle Geschwister teilen sie). */
  channexPropertyId?: string | null;
  /** Ob **dieses** Apartment beim Kanalmanager verkauft wird — die Bedingung für ARI und OTA-Anzeige. */
  channexUnitPublished: boolean;
  /** Channex-Sync-Status — null=nie geprueft, "ok"=zuletzt gefunden, "stale"=in Channex nicht mehr da. */
  status: PropertyLifecycleStatus;
  isActive: boolean;
  coverFileId?: string | null;
  propertyGroupId?: string | null;
  /** Sprint-6 C.1.2: explizit zugewiesenes Invoice-Template (null = Tenant-Primary-Fallback). */
  invoiceTemplateId?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  // Sprint 11 Issue 2: Stay-Info-Felder. WiFiPassword wird hier NIE im Klartext geliefert —
  // hasWiFiPassword ist nur ein UI-Signal. Klartext lebt im Guest-Bundle (gated).
  wiFiSsid?: string | null;
  hasWiFiPassword?: boolean;
  emergencyContact?: string | null;
  /** Wo die Wohnung im Haus liegt — Layer 2, nur fuers Gastportal. */
  apartmentDescriptionDe?: string | null;
  apartmentDescriptionEn?: string | null;
  /** Wohnungsverbund; null, wenn das Apartment weder Zimmer noch Gesamtwohnung ist. */
  flatFamily?: FlatFamilyResponse | null;
}

export interface PropertyAccessMappingResponse {
  propertyId: string;
  hasStaticAccessCode: boolean;
  updatedAt: string;
}

export interface SetStaticAccessCodeRequest {
  staticAccessCode: string;
}

export interface PropertyArchiveBlocker {
  code: string;
  message: string;
  actionUrl?: string | null;
}

export interface PropertyArchivePreflightResponse {
  propertyId: string;
  canArchive: boolean;
  blockers: PropertyArchiveBlocker[];
}

export interface PropertyArchiveResponse {
  propertyId: string;
  archived: boolean;
  blockers: PropertyArchiveBlocker[];
}

/// Sprint 11 Issue 2: Stay-Info-Pflege-Request fuer `PUT /properties/{id}/stay-info`.
/// WiFiPassword: null = unveraendert, "" = explizit loeschen, sonst = setzen.
export interface UpdatePropertyStayInfoRequest {
  wiFiSsid?: string | null;
  wiFiPassword?: string | null;
  emergencyContact?: string | null;
  apartmentDescriptionDe?: string | null;
  apartmentDescriptionEn?: string | null;
}

export interface UpdatePropertyCleaningCapacityRequest {
  availableBedCount: number;
  availableSofaBedCount: number;
}

/** Woher ein Schritt kommt: geerbt, hier überschrieben, oder eigener Schritt des Apartments. */
export type ArrivalStepOrigin = 'Inherited' | 'Overridden' | 'Apartment';

/** `KeyCodeWindow` = erst 12 h vor Anreise sichtbar, wie der Türcode. */
export type ArrivalStepVisibility = 'Always' | 'KeyCodeWindow';

export interface ArrivalStepResponse {
  id: string;
  propertyGroupId: string;
  propertyId?: string | null;
  /** Der Objekt-Schritt hinter dieser Zeile; bei eigenen Apartment-Schritten null. */
  sourceStepId?: string | null;
  origin: ArrivalStepOrigin;
  position: number;
  titleDe: string;
  bodyDe: string;
  titleEn?: string | null;
  bodyEn?: string | null;
  visibility: ArrivalStepVisibility;
  /** Wahr, wenn der Gast den Schritt erst mit dem Türcode-Fenster vollständig sieht. */
  isTimeLocked: boolean;
}

export interface ArrivalPlaceholderResponse {
  token: string;
  isTimeLocked: boolean;
  description: string;
}

/**
 * Welche der beiden gepflegten Sammlungen gemeint ist. Dieselbe Mechanik — Vererbung vom Objekt,
 * Überschreiben je Apartment, Reihenfolge, Bilder —, zwei Inhalte: der Weg zur Wohnungstür und
 * die Parkmöglichkeiten in der Umgebung.
 */
export type ArrivalStepKind = 'Arrival' | 'ParkingAlternatives';

export interface ArrivalStepListResponse {
  steps: ArrivalStepResponse[];
  placeholders: ArrivalPlaceholderResponse[];
}

export interface UpsertArrivalStepRequest {
  titleDe: string;
  bodyDe: string;
  titleEn?: string | null;
  bodyEn?: string | null;
  visibility: ArrivalStepVisibility;
}

export interface DeleteArrivalStepResponse {
  removedOverrides: number;
}

/** Markierung auf einem Bild — Koordinaten relativ zur Bildfläche (0–1). */
export interface ArrivalAnnotation {
  kind: string;
  color: string;
  x: number;
  y: number;
  x2: number;
  y2: number;
}

export interface ArrivalImageResponse {
  id: string;
  arrivalStepId: string;
  position: number;
  contentType: string;
  sizeBytes: number;
  originalFileName?: string | null;
  annotations: ArrivalAnnotation[];
}

export interface ArrivalAnnotationPaletteResponse {
  kinds: string[];
  colors: string[];
  maxItems: number;
}

export interface ArrivalImageListResponse {
  images: ArrivalImageResponse[];
  palette: ArrivalAnnotationPaletteResponse;
}

export interface ArrivalStepPreviewRequest extends UpsertArrivalStepRequest {
  /** Apartment, aus dessen Sicht gerendert wird. Leer = das erste des Objekts. */
  previewPropertyId?: string | null;
  locale?: string | null;
}

export interface ArrivalPreviewApartment {
  id: string;
  name: string;
}

/** Apartments, denen für diesen Platzhalter der Wert fehlt. */
export interface ArrivalPlaceholderGap {
  token: string;
  apartments: ArrivalPreviewApartment[];
}

export interface ArrivalStepPreviewResponse {
  propertyId: string;
  propertyName: string;
  title: string;
  body: string;
  isTimeLocked: boolean;
  apartments: ArrivalPreviewApartment[];
  gaps: ArrivalPlaceholderGap[];
  unknownPlaceholders: string[];
}

/** Ein neu anzulegendes Objekt beim Apartment-Create. */
export interface NewObjectRef {
  address: AddressDto;
  name?: string;
  needsAddressReview?: boolean;
}

/** Objektwahl beim Create — XOR: genau eines von `existingObjectId` ODER `newObject` (E1). */
export interface CreatePropertyObjectRef {
  existingObjectId?: string;
  newObject?: NewObjectRef;
}

export interface CreatePropertyRequest {
  name: string;
  slug: string;
  description?: string;
  propertyType: PropertyType;
  /** Weggelassen: die des gewählten Objekts, bei einem neuen Objekt die des Workspace. */
  timezone?: string;
  objectRef: CreatePropertyObjectRef;
  occupancyAdults: number;
  occupancyChildren: number;
  bedrooms: number;
  bathrooms: number;
  currency?: string;
  cleaningFee: number;
  baseNightlyRate: number;
  autoProvisionToChannex?: boolean;
  operationalName?: string;
  availableBedCount?: number;
  availableSofaBedCount?: number;
}

/** Update trägt die Adresse direkt (Objekt-Adresse), kein ObjectRef. Entkoppelt vom Create-Contract. */
export interface UpdatePropertyRequest {
  name: string;
  description?: string;
  propertyType: PropertyType;
  timezone: string;
  address: AddressDto;
  occupancyAdults: number;
  occupancyChildren: number;
  bedrooms: number;
  bathrooms: number;
  currency: string;
  cleaningFee: number;
  baseNightlyRate: number;
  isActive: boolean;
  addressChangeMode?: 'UpdateObject' | 'MoveToNewObject' | null;
  operationalName?: string;
}
